import React from "react";
import Login from "src/components/login";
import { Navigate } from "@tanstack/react-router";
import WS from "src/api/ws";
import { SimpleAccount } from "src/api/generated/frontend_common_v1";
import { ApiCommon } from "src/api/api_common";

/** Data provided by the {@link ACCOUNT_CONTEXT} */
export type AccountContext = {
    /** The currently logged-in account */
    account: SimpleAccount;

    /** Reload the account's information */
    reset: () => void;
};

/** {@link React.Context} to access {@link SimpleAccount account information} */
const ACCOUNT_CONTEXT = React.createContext<AccountContext>({
    account: {
        display_name: "",
        uuid: "",
        preferred_username: "",
    },

    /**
     * Reset the account's information
     */
    reset: () => {},
});
ACCOUNT_CONTEXT.displayName = "AccountContext";
export default ACCOUNT_CONTEXT;

/**
 * The properties of the account provider
 */
type AccountProviderProps = {
    /** The children of the properties */
    children: React.ReactNode | Array<React.ReactNode>;
};

/**
 * The state of the account provider
 */
type AccountProviderState = {
    /** The account */
    account: SimpleAccount | "unauthenticated" | "loading";
};

/**
 * Component for managing and providing the {@link AccountContext}
 *
 * TODO: refactor into functional component
 */
export class AccountProvider extends React.Component<AccountProviderProps, AccountProviderState> {
    state: AccountProviderState = { account: "loading" };

    fetching: boolean = false;

    /**
     * Fetch the account
     */
    fetchAccount = async () => {
        // Guard against a lot of calls
        if (this.fetching) return;
        this.fetching = true;

        this.setState({ account: "loading" });

        const account = await ApiCommon.accounts.getMe();
        WS.connect(`${window.location.origin.replace("http", "ws")}/api/frontend/v1/internal/websocket`);
        window.localStorage.setItem("mail", account.preferred_username);
        // dayjs.locale(user.preferred_lang.toLocaleLowerCase());
        this.setState({ account });

        // Clear guard against a lot of calls
        this.fetching = false;
    };

    /**
     * Hook when the component mounts
     */
    componentDidMount() {
        this.fetchAccount().then();
    }

    /**
     * The render function
     *
     * @returns The rendered component
     */
    render() {
        switch (this.state.account) {
            case "loading":
                return <div></div>;
            case "unauthenticated":
                return (
                    <>
                        <Navigate to="/" />
                        <Login
                            onLogin={() => {
                                this.fetchAccount();
                            }}
                        />
                    </>
                );
            default:
                return (
                    <ACCOUNT_CONTEXT.Provider
                        value={{
                            account: this.state.account,
                            reset: this.fetchAccount,
                        }}
                    >
                        {this.props.children}
                    </ACCOUNT_CONTEXT.Provider>
                );
        }
    }
}
